// The backend uses ONE fixed operation key per site for investigations, which
// is what lets any tab find a run it did not start (see the adoption effect in
// HealthInvestigationCard). It must match OPERATION_KEY in
// lambda_handlers/api/admin/site_health_investigation.py.
export const HEALTH_INVESTIGATION_OPERATION_KEY = "health-investigation";

import { HealthInvestigation } from "@inrange/building-manager-api-client";

// Non-component helpers for the AI health-investigation surfaces, kept out of
// the component files so they can be shared by the tile, the thread modal and
// the all-sessions modal without tripping react-refresh's "only export
// components" rule.

const INVESTIGATION_STATUS_BADGES: Record<
  string,
  { bg: string; label: string }
> = {
  completed: { bg: "success", label: "Completed" },
  timeout: { bg: "warning", label: "Timed out" },
  no_answer: { bg: "warning", label: "No answer" },
  failed: { bg: "danger", label: "Failed" },
};

// A status the backend added after this build still renders its raw value
// rather than an empty badge.
export const investigationStatusBadge = (
  status: string
): { bg: string; label: string } =>
  INVESTIGATION_STATUS_BADGES[status] ?? {
    bg: "secondary",
    label: status || "Unknown",
  };

// Operator-facing explanation for the non-"completed" terminal statuses. A
// timeout run still persists whatever text the agent wrote before stopping (as
// reportMarkdown), so the answer shows this message above that partial output.
const INVESTIGATION_STATUS_MESSAGES: Record<string, string> = {
  timeout:
    "The investigation hit its time budget before finishing. Any report below is the agent's partial output.",
  no_answer: "The investigation finished without producing an answer.",
  failed: "The investigation failed to run.",
};

// Null for a completed run (its answer speaks for itself); an unrecognised
// status falls back to naming itself rather than showing nothing.
export const investigationStatusMessage = (status: string): string | null => {
  if (status === "completed") return null;
  const message = INVESTIGATION_STATUS_MESSAGES[status];
  if (message) return message;
  return status
    ? `The investigation finished with status "${status}".`
    : "The investigation finished with an unknown status.";
};

export const GENERAL_INVESTIGATION_LABEL = "General health investigation";

export const investigationTurnLabel = (run: HealthInvestigation): string =>
  run.question?.trim() || run.headline?.trim() || GENERAL_INVESTIGATION_LABEL;

// A thread of runs sharing one sessionId: question -> answer -> follow-up
// question -> answer, oldest first.
export interface InvestigationSession {
  sessionId: string;
  runs: HealthInvestigation[];
  title: string;
  latestRun: HealthInvestigation;
  costUsd: number | null;
}

// The endpoint returns runs latest-first. Bucketing in that order and keeping
// first-encounter order gives sessions most-recently-active first, while the
// runs within each session are ordered oldest-first (the order the thread
// renders in).
export const groupInvestigationSessions = (
  investigations: HealthInvestigation[]
): InvestigationSession[] => {
  const bySession = new Map<string, HealthInvestigation[]>();
  for (const run of investigations) {
    // A run predating the session model has no sessionId - treat it as its own
    // single-turn session rather than collapsing every such run into one.
    const key = run.sessionId ?? run.runId;
    const existing = bySession.get(key);
    if (existing) existing.push(run);
    else bySession.set(key, [run]);
  }
  return [...bySession.entries()].map(([sessionId, runs]) => {
    const ordered = [...runs].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
    const costs = ordered
      .map((r) => r.costUsd)
      .filter((c): c is number => typeof c === "number");
    return {
      sessionId,
      runs: ordered,
      title: investigationTurnLabel(ordered[0]),
      latestRun: ordered[ordered.length - 1],
      costUsd: costs.length > 0 ? costs.reduce((a, b) => a + b, 0) : null,
    };
  });
};

// Returns null when there's no cost to show, so callers omit the metadata
// rather than rendering a misleading "$0.00". Sub-cent runs are real, so they
// get extra precision instead of rounding to zero.
export const formatInvestigationCost = (
  costUsd: number | null | undefined
): string | null => {
  if (typeof costUsd !== "number" || !isFinite(costUsd)) return null;
  return costUsd >= 0.01 ? `$${costUsd.toFixed(2)}` : `$${costUsd.toFixed(4)}`;
};
